/**
 * Box-Select Controller
 *
 * Wraps `AreaSelector` + `CoordinateTransformer` for tools that need a
 * rectangular drag-select. Handles the modifier-flag bookkeeping
 * (Shift = add, Ctrl/Cmd = remove, none = replace) and exposes the
 * canvas-pixel rect on `finish()` so callers can run their own hit-test
 * against whatever they care about. The actual "what's in the box" check
 * stays with the consumer because it differs per tool (skeleton edges +
 * nodes vs. door/window annotations vs. ...).
 *
 * Lazy semantics: `start()` will lazily create the wrapped `AreaSelector`
 * the first time it's called, mirroring the pattern the skeleton tool
 * used originally — most tool sessions never box-select, so we don't
 * want to spin up the DOM overlay until it's actually needed.
 */

import * as THREE from 'three';
import { AreaSelector, BoxStyle } from './AreaSelector';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import { Viewer } from '../../../../viewer/viewer';

export type BoxSelectMode = 'replace' | 'add' | 'remove';

export interface BoxRect {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface BoxSelectFinishResult {
  mode: BoxSelectMode;
  rect: BoxRect;
}

export interface BoxSelectControllerOptions {
  /** Visual styling forwarded to the underlying AreaSelector. */
  boxStyle?: BoxStyle;
}

export class BoxSelectController {
  private viewer: Viewer;
  private options: BoxSelectControllerOptions;
  private areaSelector: AreaSelector | null = null;
  private coordinateTransformer: CoordinateTransformer | null = null;
  private mode: BoxSelectMode = 'replace';
  private active = false;

  constructor(viewer: Viewer, options: BoxSelectControllerOptions = {}) {
    this.viewer = viewer;
    this.options = options;
  }

  /**
   * Begin a box-select drag. Captures the modifier flags from the event so
   * the matching `finish()` knows whether to replace / add to / remove from
   * the caller's selection. The caller is expected to also block orbit
   * controls (or handle it via the tool's drag plumbing) — this controller
   * does not touch input handling.
   */
  start(event: MouseEvent): BoxSelectMode {
    this.ensureSelector();
    if (!this.areaSelector) return 'replace';
    const ctrl = event.ctrlKey || event.metaKey;
    this.mode = ctrl ? 'remove' : event.shiftKey ? 'add' : 'replace';
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const startPx = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    this.areaSelector.startSelection(startPx);
    this.active = true;
    return this.mode;
  }

  /** Forward cursor movement to the underlying selector. */
  update(event: MouseEvent): void {
    if (!this.areaSelector || !this.active) return;
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const px = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    this.areaSelector.updateSelection(px);
  }

  /**
   * End the drag. Returns the captured mode + the canvas-pixel rect of the
   * selection box, or null if no selection was active. The caller does the
   * actual hit-test against whatever items they want to select. After
   * returning, the underlying selector's UI is hidden.
   */
  finish(): BoxSelectFinishResult | null {
    if (!this.areaSelector || !this.active) return null;
    const box = this.areaSelector.getSelectionBoxElement();
    const canvasRect = this.viewer.renderer.domElement.getBoundingClientRect();
    const boxRect = box.getBoundingClientRect();
    const result: BoxSelectFinishResult = {
      mode: this.mode,
      rect: {
        minX: boxRect.left - canvasRect.left,
        maxX: boxRect.right - canvasRect.left,
        minY: boxRect.top - canvasRect.top,
        maxY: boxRect.bottom - canvasRect.top,
      },
    };
    this.areaSelector.cancelSelection();
    this.active = false;
    return result;
  }

  /** Abort the current drag without producing a rect. */
  cancel(): void {
    if (this.areaSelector && this.active) this.areaSelector.cancelSelection();
    this.active = false;
  }

  isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    if (this.areaSelector) {
      this.areaSelector.dispose();
      this.areaSelector = null;
    }
    this.coordinateTransformer = null;
    this.active = false;
  }

  private ensureSelector(): void {
    if (this.areaSelector && this.coordinateTransformer) return;
    this.coordinateTransformer = new CoordinateTransformer(this.viewer);
    this.areaSelector = new AreaSelector(this.viewer, this.coordinateTransformer, {
      // No m² thresholds — we just want the visual rectangle, not an
      // area-warning UI.
      showLabel: false,
      showWarning: false,
      boxStyle: this.options.boxStyle,
    });
  }
}
